import _ from 'lodash';

import { THEME } from '../../constants/notification';
import { getSelectedOrganizationId } from '../../selectors/organizations/organizations';
import { projectService } from '../../transport';
import { Project } from '../../types/entities';
import { errorLogger } from '../../utils/error-logger/error-logger';
import i18n from '../../utils/i18n';
import { BI_ERRORS } from '../../utils/statistics/bi-error-logger/bi-errors';
import { getProjectByRoute } from '../projects/projects.selectors';
import { Dispatch, GetState } from '../redux.types';

import { buildMemoizedLoadFn } from './router/utils/utils';
import { serviceNames, statuses } from '../../constants/project';

export const PROJECTS_ACTION_NAMES = {
  PROJECTS_SUCCESS: 'PROJECTS_SUCCESS',

  PROJECT_ADD_SUCCESS: 'PROJECT_ADD_SUCCESS',
  PROJECT_ADD_ERROR: 'PROJECT_ADD_ERROR',

  PROJECT_DELETE_SUCCESS: 'PROJECT_DELETE_SUCCESS',
  PROJECT_DELETE_ERROR: 'PROJECT_DELETE_ERROR',

  UPDATE_PROJECT_STATUSES_START: 'UPDATE_PROJECT_STATUSES_START',
  UPDATE_PROJECT_STATUSES_SUCCESS: 'UPDATE_PROJECT_STATUSES_SUCCESS',
  UPDATE_PROJECT_STATUSES_ERROR: 'UPDATE_PROJECT_STATUSES_ERROR',
};

const projectsSuccess = (projects) => ({
  type: PROJECTS_ACTION_NAMES.PROJECTS_SUCCESS,
  payload: projects,
});

const deleteProjectSuccess = (projectId) => ({
  type: PROJECTS_ACTION_NAMES.PROJECT_DELETE_SUCCESS,
  payload: projectId,
});

const deleteProjectError = (payload, name) => ({
  type: PROJECTS_ACTION_NAMES.PROJECT_DELETE_ERROR,
  payload,
  notification: {
    message: i18n.t('notifications.project.delete.error', { name }),
    theme: THEME.ERROR,
  },
});

const addProjectSuccess = (payload) => ({
  type: PROJECTS_ACTION_NAMES.PROJECT_ADD_SUCCESS,
  payload,
});

const addProjectError = (payload, name) => ({
  type: PROJECTS_ACTION_NAMES.PROJECT_ADD_ERROR,
  payload,
  notification: {
    message: i18n.t('notifications.project.create.error', { name }),
    theme: THEME.ERROR,
  },
});

export const updateProjectStatuses =
  ({ organizationId }: { organizationId: string }) =>
  (dispatch: Dispatch) => {
    return dispatch({
      types: [
        PROJECTS_ACTION_NAMES.UPDATE_PROJECT_STATUSES_START,
        PROJECTS_ACTION_NAMES.UPDATE_PROJECT_STATUSES_SUCCESS,
        PROJECTS_ACTION_NAMES.UPDATE_PROJECT_STATUSES_ERROR,
      ],
      callApi: () => projectService.getProjects({ organizationId }),
    });
  };

function projectIdFromDomain(domain: string) {
  return domain.replace('.appspot.com', '');
}

export const getProjects = (organizationId: string) => (dispatch) => {
  return projectService.getProjects({ organizationId }).then(
    // TODO: move sorting from client to server side
    (response) => {
      const projects = response.data.payload;
      if (projects && projects.length) {
        projects.map((project: Project) => {
          const googleProjectId =
            (project.domain && projectIdFromDomain(project.domain)) || '';
          const projectFilesHost = `${googleProjectId}.wixmp.com`;

          project.filesHost = projectFilesHost;
          project.services = [
            {
              host: projectFilesHost,
              name: serviceNames.file,
              status: statuses.READY,
              type: serviceNames.file,
            },
            {
              host: `img-${googleProjectId}.wixmp.com`,
              name: serviceNames.image,
              status: statuses.READY,
              type: serviceNames.image,
            },
            {
              host: `packager-${googleProjectId}.wixmp.com`,
              name: serviceNames.videoPackager,
              status: statuses.READY,
              type: serviceNames.videoPackager,
            },
          ];
        });
      }

      dispatch(projectsSuccess(_.orderBy(projects, ['dateCreated'], ['desc'])));

      return projects;
    },
  );
};

export const getProjectsCached = buildMemoizedLoadFn(getProjects);

interface DeleteProjectParams {
  organizationId?: string;
  project?: Project;
}

export const deleteProject =
  ({ organizationId, project }: DeleteProjectParams = {}) =>
  (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const state = getState();

    organizationId = organizationId || getSelectedOrganizationId(state);
    project = project || getProjectByRoute(state);

    const { id: projectId, name: projectName } = project;

    return projectService.deleteProject({ organizationId, projectId }).then(
      () => {
        dispatch(deleteProjectSuccess(projectId));
      },
      ({ response }) => {
        dispatch(
          deleteProjectError(_.get(response, 'data.payload'), projectName),
        );

        errorLogger.logError({
          errorDescription: BI_ERRORS.projects.deleteProjectFailed,
          otherParams: { response },
        });

        return Promise.reject(response);
      },
    );
  };

export const addProject =
  (organizationId: string, project: Project) => (dispatch: Dispatch) => {
    return projectService.createProject({ organizationId, project }).then(
      (response) => dispatch(addProjectSuccess(response.data.payload)),
      ({ response }) => {
        dispatch(
          addProjectError(_.get(response, 'data.payload'), project.name),
        );

        errorLogger.logError({
          errorDescription: BI_ERRORS.projects.addProjectFailed,
          otherParams: { response },
        });

        return Promise.reject(response);
      },
    );
  };
